import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Paperclip, Trash2, Download, Upload as UploadIcon } from "lucide-react";

const humanSize = (n) => {
  if (!n && n !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = Number(n);
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

/**
 * Attachments panel for a given target (contact / category / event).
 * Renders a list of existing attachments with Download + Delete + a compact
 * upload form (file picker + optional label).
 */
export default function AttachmentsPanel({ targetKind, targetId, title = "Documents" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/attachments", {
        params: { target_kind: targetKind, target_id: targetId },
      });
      setItems(data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [targetKind, targetId]);

  useEffect(() => {
    load();
  }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("target_kind", targetKind);
      fd.append("target_id", targetId);
      fd.append("label", label);
      fd.append("file", file);
      await api.post("/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      setLabel("");
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDownload = async (att) => {
    try {
      const resp = await api.get(`/attachments/${att.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const onDelete = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (!targetId) {
    return (
      <div className="text-xs italic text-neutral-400 border border-dashed border-[var(--rw-border)] rounded-[4px] px-3 py-2">
        Save this record first, then you can attach documents.
      </div>
    );
  }

  return (
    <div className="border border-[var(--rw-border)] rounded-[4px] bg-white" data-testid="attachments-panel">
      <div className="flex items-center gap-2 px-3 py-2 bg-[var(--rw-surface-2)] border-b border-[var(--rw-border)]">
        <Paperclip size={12} className="text-neutral-500" />
        <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-600">
          {title}
        </span>
        <span className="text-xs text-neutral-500">· {items.length}</span>
      </div>

      <div className="p-3 space-y-2">
        {loading && <div className="text-xs text-neutral-400">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-xs text-neutral-400 italic">No documents attached yet.</div>
        )}
        {items.map((att) => (
          <div
            key={att.id}
            className="flex items-center gap-2 text-xs border border-[var(--rw-border)] rounded-[4px] px-2 py-1.5 bg-[var(--rw-surface-2)]/40 hover:bg-[var(--rw-surface-2)]"
            data-testid={`att-row-${att.id}`}
          >
            <Paperclip size={11} className="text-neutral-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{att.filename}</div>
              {(att.label || att.size) && (
                <div className="text-neutral-500 text-[11px] truncate">
                  {att.label ? `${att.label} · ` : ""}
                  {humanSize(att.size)}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDownload(att)}
              className="p-1 rounded hover:bg-white"
              title="Download"
              data-testid={`att-download-${att.id}`}
            >
              <Download size={12} />
            </button>
            <button
              type="button"
              onClick={() => onDelete(att)}
              className="p-1 rounded hover:bg-white text-neutral-400 hover:text-[#E5484D]"
              title="Delete"
              data-testid={`att-delete-${att.id}`}
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}

        <div className="pt-2 border-t border-[var(--rw-border)] mt-2">
          <label className="block text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-500 mb-1">
            Label <span className="font-normal normal-case text-neutral-400">(optional — e.g. "2024 tax return")</span>
          </label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Financial statement"
            className="mb-2"
            data-testid="att-label-input"
          />
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              onChange={onUpload}
              className="hidden"
              disabled={uploading}
              data-testid="att-file-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="att-upload-btn"
            >
              <UploadIcon size={12} className="mr-2" />
              {uploading ? "Uploading…" : "Upload document"}
            </Button>
            <span className="text-[11px] text-neutral-400">up to 15 MB</span>
          </div>
        </div>
      </div>
    </div>
  );
}
